import React from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  SocialHistoryDTO,
} from 'dtos';
import Select from 'core/components/Select';
import Row from 'pages/Dashboard/components/Row';
import {
  optionsByCategory,
} from 'core/constants';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';

export type SocialHistory = {
  id: number;
  label?: string;
  key: keyof SocialHistoryDTO;
  selectOptions: string[];
  chartDetailLabel: string;
};

type Props = {
  info: SocialHistory;
};

const pairedUsageFields: Partial<Record<keyof SocialHistoryDTO, keyof SocialHistoryDTO>> = {
  tobaccoUsedInLast12Months: 'alcoholConsumedInLast12Months',
  alcoholConsumedInLast12Months: 'tobaccoUsedInLast12Months',
};

function hasAnswer(value?: string | null): boolean {
  return notEmptyString(value ?? '');
}

function isUsageAnswerValid(
  value: string | null | undefined,
  pairedField: keyof SocialHistoryDTO | undefined,
  socialHistory: SocialHistoryDTO | undefined,
): boolean {
  return isNil(pairedField)
    || hasAnswer(value)
    || !hasAnswer(socialHistory?.[pairedField]);
}

export default function SocialHistoryInfo({
  info,
}: Props) {
  const { control, getValues } = useFormContext();
  const fieldKey = `socialHistory.${info.key}`;
  const pairedField = pairedUsageFields[info.key];

  return (
    <Row
      className="!border-0 !p-0 !items-center w-[500px]"
      title={info.label ?? ''}
      classes={{ title: '!text-gray-500 !font-semibold' }}
    >
      <div className="flex flex-row">
        <Controller
          name={fieldKey}
          control={control}
          rules={{
            validate: (value) => isUsageAnswerValid(
              value,
              pairedField,
              getValues('socialHistory') as SocialHistoryDTO | undefined,
            ),
          }}
          render={({ field: { value, ...field }, fieldState: { error } }) => (
            <Select<string>
              {...field}
              className="w-44"
              id={`social-history-${info.key}`}
              placeholder="Select"
              value={info.selectOptions.find((option) => option === value)}
              options={info?.selectOptions ?? []}
              getLabel={(option) => optionsByCategory[info.key][option]?.label}
              getValue={(option) => option}
              error={!isNil(error)}
              showErrorText={false}
            />
          )}
        />
      </div>
    </Row>
  );
}
